/**
 * InstantAvatar - Zero-flash avatar component
 * Attempts to preload images reactively if not already cached
 * Supports different fallback modes to prevent flash
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { isAvatarPreloaded, preloadAvatar } from "@/lib/avatarPreloader";

interface InstantAvatarProps {
  avatarUrl: string | null;
  fallbackText: string;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * Fallback behavior when image is not preloaded:
   * - "none": render transparent placeholder (no flash)
   * - "gradient": render neutral gradient circle (no initials)
   * - "initials": render first letter on gradient (default for non-critical UI)
   */
  fallback?: "none" | "gradient" | "initials";
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10", 
  lg: "h-16 w-16",
  xl: "h-24 w-24 md:h-32 md:w-32"
};

const fontSizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-xl",
  xl: "text-2xl md:text-3xl"
};

export const InstantAvatar = React.memo(function InstantAvatar({
  avatarUrl,
  fallbackText,
  className,
  size = "md",
  fallback = "initials"
}: InstantAvatarProps) {
  // Track if image is ready to display
  const [isReady, setIsReady] = React.useState(() => isAvatarPreloaded(avatarUrl));
  
  const fallbackLetter = fallbackText[0]?.toUpperCase() || "U";
  
  // Reactively preload the avatar if not already cached
  React.useEffect(() => {
    if (!avatarUrl) {
      setIsReady(false);
      return;
    }
    
    // Check if already preloaded
    if (isAvatarPreloaded(avatarUrl)) {
      setIsReady(true);
      return;
    }
    
    // Not preloaded yet - trigger preload and update state when ready
    setIsReady(false);
    preloadAvatar(avatarUrl).then((result) => {
      if (result) {
        setIsReady(true);
      }
    }).catch(() => {
      // Keep showing fallback on error
    });
  }, [avatarUrl]);
  
  // If we have a URL and it's ready, show the image
  const showImage = avatarUrl && isReady;
  
  return (
    <div 
      className={cn(
        sizeClasses[size],
        "relative rounded-full overflow-hidden flex-shrink-0",
        className
      )}
    >
      {showImage ? (
        <img
          src={avatarUrl}
          alt={fallbackText}
          className="w-full h-full object-cover"
          // No loading needed - image is already in browser cache
          loading="eager"
          decoding="sync"
        />
      ) : fallback === "none" ? (
        // Transparent placeholder - prevents any flash
        <div className="w-full h-full bg-transparent" />
      ) : fallback === "gradient" ? (
        // Neutral gradient without initials - subtle placeholder
        <div className="w-full h-full bg-gradient-to-br from-muted to-muted-foreground/20" />
      ) : (
        // Initials fallback - only for non-critical UI
        <div className={cn(
          "w-full h-full flex items-center justify-center",
          "bg-gradient-to-br from-blue-600 to-purple-600 text-white font-medium",
          fontSizeClasses[size]
        )}>
          {fallbackLetter}
        </div>
      )}
    </div>
  );
});

InstantAvatar.displayName = "InstantAvatar";
